// NPC dialog (qa-runs#1167): one-way lines the DM (or a co-DM with `tokens`) has a token, or a free name such as
// *Narrator*, say at the table. Not chat (design record §3.2): players never send one.
//
// A line is written to the table's own log as a `dialog.say`, stamped by the server with WHO HEARD IT and how each of
// them heard it — the speaker named as that player sees it (`nameShown`; an unrevealed creature is *Unknown creature*)
// and whether they get a bubble (a token they cannot see, `canSee`, gives none: *A voice*, or the DM's name). So a
// replay, a reload or next session's sidebar reads exactly what was heard then, and a player left out of a line is in
// no list: hidden means absent (design/22), their payload holds no trace of it. A `dialog.delete` carries the same
// list, so the line goes from every sidebar that holds it and reaches nobody else.
//
// Lines a cue says (qa-runs#1168) are ordinary `dialog.say`s carrying the cue's id, so "once per session" and "once
// ever" are read from the log, and a replay never says one twice.
import { canSee } from './objects.js';
// `nameShown` (project.ts) is read inline: project.ts imports objects.ts, which imports this module through cues.ts.
const nameShown = (o, mine) => mine || o.pc || o.shares.name;
export const DIALOG_LIMITS = { text: 500, name: 60, to: 50, prepared: 20 };
export const UNKNOWN_SPEAKER = 'Unknown creature';
export const A_VOICE = 'A voice';
export const DIALOG_GONE = 'That line is gone.';
export const DIALOG_NOBODY = 'Nobody is on that scene to hear it.';
export const DIALOG_KINDS = ['dialog.say', 'dialog.delete'];
export const isDialogBody = (b) => DIALOG_KINDS.includes(b.kind);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isIds = (v, max) => Array.isArray(v) && v.length <= max && v.every(isUuid) && new Set(v).size === v.length;
/** A line's words: plain text, trimmed, 1–500 characters (a long speech is several lines). */
export function cleanLineText(v) {
    if (typeof v !== 'string')
        return null;
    const t = v.replace(/\r\n?/g, '\n').trim();
    return t.length >= 1 && t.length <= DIALOG_LIMITS.text ? t : null;
}
/** A name the DM typed: trimmed, at most 60 characters; empty is none. */
export function cleanSpeakerName(v) {
    if (v === undefined || v === null)
        return undefined;
    if (typeof v !== 'string')
        return null;
    const t = v.trim();
    if (!t)
        return undefined;
    return t.length <= DIALOG_LIMITS.name ? t : null;
}
function parseHeard(v) {
    if (!Array.isArray(v) || v.length > 500)
        return null;
    const out = [];
    for (const h of v) {
        if (!isObj(h) || !isUuid(h.id) || typeof h.name !== 'string' || h.name.length > 200 || typeof h.bubble !== 'boolean')
            return null;
        out.push({ id: h.id, name: h.name, bubble: h.bubble });
    }
    return out;
}
export function parseDialogLine(v) {
    if (!isObj(v) || !isUuid(v.id))
        return null;
    const text = cleanLineText(v.text);
    if (text === null || text !== v.text)
        return null;
    const heard = parseHeard(v.heard);
    if (!heard || typeof v.dmName !== 'string' || v.dmName.length > 200)
        return null;
    let speaker;
    if (v.speaker !== undefined) {
        if (!isObj(v.speaker) || !isUuid(v.speaker.scene) || !isUuid(v.speaker.objectId))
            return null;
        speaker = { scene: v.speaker.scene, objectId: v.speaker.objectId };
    }
    const name = cleanSpeakerName(v.name);
    if (name === null)
        return null;
    if (v.to !== undefined && (!isIds(v.to, DIALOG_LIMITS.to) || !v.to.length))
        return null;
    if (v.cue !== undefined && !isUuid(v.cue))
        return null;
    return {
        id: v.id, text, heard, dmName: v.dmName,
        ...(speaker ? { speaker } : {}), ...(name ? { name } : {}), ...(v.to ? { to: [...v.to] } : {}), ...(v.cue ? { cue: v.cue } : {}),
    };
}
export function parseDialogBody(b) {
    if (b.kind === 'dialog.say') {
        const line = parseDialogLine(b.line);
        return line ? { kind: 'dialog.say', line } : null;
    }
    if (b.kind === 'dialog.delete')
        return isUuid(b.id) && isIds(b.heard, 500) ? { kind: 'dialog.delete', id: b.id, heard: [...b.heard] } : null;
    return null;
}
/**
 * How each listener hears a line from `speaker` on `scene` (null: no token): the name the DM gave it wins; else a token
 * the player sees is named as they see it (its name when `nameShown`, *Unknown creature* when not); a token they cannot
 * see gets no bubble and speaks as *A voice*; a line with no token and no name has none.
 */
export function dialogHeard(listeners, speaker, scene, name) {
    return [...new Set(listeners)].map((id) => {
        if (!speaker || !scene)
            return { id, name: name ?? '', bubble: false };
        const sees = canSee(speaker, { role: 'player', id }, scene);
        if (!sees)
            return { id, name: name ?? A_VOICE, bubble: false };
        return { id, name: name ?? (nameShown(speaker, speaker.owners.includes(id)) ? speaker.name : UNKNOWN_SPEAKER), bubble: true };
    });
}
/** The DM's name for the speaker: the given name, the token's own, or none. */
export const dmSpeakerName = (speaker, name) => name ?? speaker?.name ?? '';
/** A line as one player has it, or null when they did not hear it (hidden means absent). */
export function playerDialogLine(line, viewerId, at) {
    const h = line.heard.find((x) => x.id === viewerId);
    if (!h)
        return null;
    return {
        id: line.id, text: line.text, name: h.name, at,
        ...(h.bubble && line.speaker ? { objectId: line.speaker.objectId, scene: line.speaker.scene } : {}),
        ...(line.to ? { toYou: true } : {}),
    };
}
/** The DM's sidebar line. */
export function dmDialogLine(line, at) {
    return {
        id: line.id, text: line.text, name: line.name ?? line.dmName, at, heard: line.heard.map((h) => ({ ...h })), dmName: line.dmName,
        ...(line.speaker ? { objectId: line.speaker.objectId, scene: line.speaker.scene } : {}),
        ...(line.cue ? { cue: line.cue } : {}),
    };
}
/** How long a bubble stays: about a second per five words, 3 to 10 seconds. */
export function bubbleMs(text) {
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return Math.min(10_000, Math.max(3_000, Math.round((words / 5) * 1000)));
}
/** The table's lines folded from its log, oldest first, deletions applied: what the sidebar reads on a load. */
export function foldDialogLog(rows) {
    const out = new Map();
    for (const r of rows) {
        if (r.body.kind === 'dialog.say')
            out.set(r.body.line.id, { line: r.body.line, at: r.at });
        else
            out.delete(r.body.id);
    }
    return [...out.values()];
}
//# sourceMappingURL=dialog.js.map